import { Component, computed, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition
} from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import {
  DistributionSettings,
  HttpRequestError,
  MultiStepForm,
  openConfirmDialog,
  PlanDetails,
  RolloutPlanDetailsDto,
  RolloutPlanStepId,
  RolloutPlanStore,
  RolloutPlanSummary,
  RolloutPlanSummaryService,
  TargetProductGroups,
  transformRolloutDataToRequestBody,
  useRolloutPlanStore
} from '@ottobock/remote-update/domain';
import { LogService } from '@ottobock/shared/domain';

@Component({
  selector: 'remote-update-feature-remote-update-dashboard-create-rollout-plan',
  imports: [
    MultiStepForm,
    PlanDetails,
    TargetProductGroups,
    DistributionSettings,
    RolloutPlanSummary,
    MatProgressBar
  ],
  templateUrl: './create-rollout-plan.html',
  styleUrl: './create-rollout-plan.css'
})
export class CreateRolloutPlan implements OnInit {
  @ViewChild(DistributionSettings) productGroupComponent?: DistributionSettings;
  store = inject(RolloutPlanStore);
  private apiRolloutStore = inject(useRolloutPlanStore);
  private _snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<CreateRolloutPlan>);
  private rolloutPlanSummaryService = inject(RolloutPlanSummaryService);
  private router = inject(Router);
  private logService = inject(LogService);
  private allFormData = computed(() => this.store.getAllFormData());

  rolloutPlanMenu = this.store.menuItems;
  isEditingRolloutPlan = computed(() => this.store.isEditingRolloutPlan());

  isLoading = computed(() => this.apiRolloutStore.uploadRolloutPlan().loading);

  horizontalPosition: MatSnackBarHorizontalPosition = 'start';
  verticalPosition: MatSnackBarVerticalPosition = 'bottom';

  private rolloutPlanEditData = inject<{
    rolloutPlanDetails?: RolloutPlanDetailsDto;
  }>(MAT_DIALOG_DATA);

  get isLastStep(): boolean {
    const order = this.store.stepOrder();
    return this.currentSection === order[order.length - 1];
  }

  get currentSection(): string {
    return this.store.currentSection();
  }

  get actionButtonLabel(): boolean {
    return this.isLastStep;
  }

  get canProceed(): boolean {
    return this.store.canNavigateNext();
  }

  ngOnInit(): void {
    const rolloutPlanDetails = this.rolloutPlanEditData.rolloutPlanDetails;
    if (rolloutPlanDetails) {
      const detailsOfRollouPlan = {
        packageVersion: rolloutPlanDetails.packageVersion,
        productGroupPercentage: rolloutPlanDetails.productGroupPercentages,
        status: rolloutPlanDetails.status,
        packageId: rolloutPlanDetails.packageId,
        strategy: rolloutPlanDetails.strategy
      };
      this.store.editRolloutPlan(detailsOfRollouPlan);
    }
  }

  goToNextStep(): void {
    const step = this.currentSection;

    switch (step) {
      case RolloutPlanStepId.PlanDetails:
        if (!this.store.planDetails().isValid) {
          this.store.updatePlanDetails(
            { isValid: false },
            this.store.errors().planDetails || 'Select a firmware package to roll out.'
          );
          return;
        }
        break;

      case RolloutPlanStepId.TargetProductGroups:
        if (!this.store.productGroups().isValid) {
          this.store.updateProductGroups(
            { isValid: false },
            this.store.errors().targetProductGroups ||
              'No product groups added yet. Add at least one to continue.'
          );
          return;
        }
        break;

      case RolloutPlanStepId.DistributionSettings:
        if (!this.store.distributionSettings().isValid) {
          this.productGroupComponent?.markAttempted();
          return;
        }
        break;

      case RolloutPlanStepId.summary:
        this.handleRolloutPlanSubmission();
        return;
      default:
        return;
    }
    this.store.goToNextStep();
  }

  private async submitCreateRolloutPlanForm() {
    try {
      const rolloutCreationData = transformRolloutDataToRequestBody(this.allFormData());
      await this.rolloutPlanSummaryService.loadRolloutPlan(rolloutCreationData, {
        refetchAllPlans: true
      });

      this.dialogRef.close('success');
      this.router.navigate(['/rollout-plan']);
      this.store.resetFormData();
      this._snackBar.open('Rollout plan created', 'Close');
    } catch (error) {
      const err = error as HttpRequestError<unknown>;
      this.logService.error('Error during rollout plan creation.', err?.statusText);
      const message = 'Something went wrong. Try again.';
      this._snackBar.open(message, 'Close');
    }
  }

  private async submitUpdateForm() {
    const rolloutUpdateData = transformRolloutDataToRequestBody(this.allFormData());
    try {
      if (this.rolloutPlanEditData.rolloutPlanDetails?.rolloutPlanId) {
        await this.rolloutPlanSummaryService.updateRolloutPlan(
          this.rolloutPlanEditData.rolloutPlanDetails.rolloutPlanId,
          {
            refetchAllPlans: true,
            metadata: rolloutUpdateData
          }
        );
      }
      this.dialogRef.close('success');
      this.router.navigate(['/rollout-plan']);
      this.store.resetFormData();
      this._snackBar.open('Rollout plan updated successfully!', 'Close');
    } catch (error: unknown) {
      this.logService.error('Error occured while updating the rollout plan', error);
      const message = 'Something went wrong. Try again.';
      this._snackBar.open(message, 'Close');
    }
  }

  private openConfirCreateDialog() {
    const confirmDialog = openConfirmDialog(this.dialog, {
      message:
        'Are you sure you want to create this rollout plan?\n\nIt will be set to the state “Draft”. Please activate it manually.',
      leftButton: 'Cancel',
      rightButton: 'Create',
      height: '250px',
      discourageConfirmation: false
    });

    confirmDialog.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.submitCreateRolloutPlanForm();
      }
    });
  }

  onCancel() {
    const confirmDialog = openConfirmDialog(this.dialog, {
      message: 'Are you sure you want to cancel? You might lose your unsaved changes.',
      leftButton: 'Leave',
      rightButton: 'Stay on page',
      discourageConfirmation: true
    });

    confirmDialog.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.dialogRef.close('cancelled');
        this.store.resetFormData();
      }
    });
  }

  private handleRolloutPlanSubmission(): void {
    if (this.isEditingRolloutPlan()) {
      this.submitUpdateForm();
    } else {
      this.openConfirCreateDialog();
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification(event: BeforeUnloadEvent) {
    const { planDetails, distributionSettings } = this.allFormData();
    const isAnyStepValid = [planDetails, distributionSettings].some(step => step.isValid);
    if (isAnyStepValid) event.preventDefault();
  }
}
